'use client'

import Link from 'next/link'
import { Fragment, useContext } from 'react'
import Accordion from 'src/components/design-system/Acordion'
import Typography from 'src/components/design-system/Typography'
import { AuthorListItem } from 'src/components/authors/list/AuthorListItem'
import DownloadFile from 'src/components/items/DownloadFile'
import LINKS from 'src/_helpers/links'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { formatFileSize } from 'src/_helpers/formatFileSize'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { orderResourcesByUuid } from 'src/_helpers/orderResourcesByUuid'
import { GlobalContext } from 'src/components/layout/GlobalContext'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import DownloadIcon from 'public/icons/interface/download.svg'
import FileIcon from 'public/icons/interface/file2.svg'

export function buildPublicationAuthorColumnBorders(index) {
  const classes = []

  if (index % 2 === 1) {
    classes.push('t:border-l', 't:border-basic-gray8', 't:pl-24 d:pl-80')
  }

  return classes.join(' ')
}

export function formatPublicationDate(rawDate) {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
  }

  return typeof rawDate === 'string' ? rawDate : null
}

export function buildPublicationDownloadEntries(files, locale, fallbackTitle = '') {
  return (Array.isArray(files) ? files : [])
    .map((file) => {
      const media = file?.media || file?.file || file
      const mediaUrl = file?.downloadUrl
        ? getMediaUrl(file.downloadUrl)
        : media?.url
          ? getMediaUrl(media.url, media.version)
          : null

      if (!mediaUrl) return null

      const translation = getPreferredTranslation(file, locale) || {}
      const title = translation?.title || file?.title || fallbackTitle
      const fileSize = file?.fileMetadata?.filesize || media.size
      const extension = file?.fileMetadata?.extension || media.extension || media.ext || ''
      const size = fileSize ? formatFileSize(fileSize) : null
      const meta = [extension && extension.toUpperCase(), size].filter(Boolean).join(' ')

      return { href: mediaUrl, title, meta, openInBrowser: extension.toLowerCase() === 'pdf' }
    })
    .filter(Boolean)
}

export function PublicationAuthorsAccordion({ authors, locale, label }) {
  // Na domenie czasopisma trasy znają domyślny język czasopisma — trasy publisphere gubiłyby prefiks
  // języka (`/autorzy/...` zamiast `/pl/autorzy/...`) i każde wejście kończyłoby się przekierowaniem.
  const { journalDomainRoutes } = useContext(GlobalContext)
  const authorRoute = journalDomainRoutes?.AUTHORS?.DETAIL || LINKS.AUTHORS.DETAIL
  const items = Array.isArray(authors) ? authors : []

  if (items.length === 0) {
    return null
  }

  return (
    <Accordion type="block" color="dark-blue">
      <Accordion.Item>
        <Accordion.Trigger variant="button" icon="plus">
          {label}
        </Accordion.Trigger>
      </Accordion.Item>
      <Accordion.Content>
        <div className="grid gap-40 t:grid-cols-2 pt-24 d:w-[82%]">
          {items.map((author, index) => {
            const fullname = author?.fullName
            const orcid = author?.orcid
            const affiliations = orderResourcesByUuid(author?.affiliations, author?.affiliationOrder)
            const columnBorders = buildPublicationAuthorColumnBorders(index)
            const detailUrl = buildLocalizedPath(authorRoute, locale, { slug: author?.slug }) || ' '

            return (
              <AuthorListItem
                key={author?.uuid || index}
                name={fullname}
                orcid={orcid}
                affiliations={affiliations}
                detailUrl={detailUrl}
                locale={locale}
                className={`pb-16 ${columnBorders}`}
              />
            )
          })}
        </div>
      </Accordion.Content>
    </Accordion>
  )
}

export function PublicationStatistics({ statistics, color = 'default' }) {
  const translate = useTranslateEntry()

  if (!statistics) {
    return null
  }

  const separatorColor = color === 'journalDomain' ? 'bg-basic-gray7' : 'bg-dark-blue-dark-blue6'
  const metrics = [
    ['T_NUMBER_OF_VIEWS', statistics.views_count],
    ['T_NUMBER_OF_DOWNLOADS', statistics.downloads_count],
    ['T_CROSSREF_CITATIONS', statistics.citations],
    ['T_ALTMETRIC_SCORE', statistics.altmetric],
  ]

  return (
    <>
      <div className="flex flex-col gap-y-16">
        <div className="flex flex-col d:flex-row gap-y-24 gap-x-30">
          {metrics.map(([label, value], index) => (
            <Fragment key={label}>
              {index > 0 && <hr className={`col-span-full hidden h-26 w-1 d:block ${separatorColor}`} />}
              <p className="flex gap-x-8 items-center">
                <Typography variant="body2" component="span" color="text-basic-black">
                  {translate(label)}:
                </Typography>
                <Typography variant="h4-caps-bold" component="span" color="text-basic-black">
                  {value ?? '—'}
                </Typography>
              </p>
            </Fragment>
          ))}
        </div>
      </div>
      <hr className={`col-span-full h-1 w-full ${separatorColor}`} />
    </>
  )
}

export function PublicationDownloads({ entries, title, downloadLabel, showFileTitle = false, color = 'default' }) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return null
  }

  return (
    <>
      <Typography variant="h3-caps-bold" component="h2" color="text-basic-black">
        {title}
      </Typography>

      <div className="grid grid-cols-1 gap-x-40 t:gap-y-80 gap-y-24 t:grid-cols-2 d:grid-cols-4">
        {entries.map((item, index) => (
          <DownloadFile
            key={`${item.href}-${index}`}
            href={item.href}
            title={showFileTitle ? item.title : ''}
            meta={item.meta}
            downloadLabel={downloadLabel}
            DownloadIcon={DownloadIcon}
            FileIcon={FileIcon}
            color={color}
            openInBrowser={item.openInBrowser}
          />
        ))}
      </div>
      <hr
        className={`col-span-full h-1 w-full ${color === 'journalDomain' ? 'bg-basic-gray7' : 'bg-dark-blue-dark-blue6'}`}
      />
    </>
  )
}

export function DoiInfoLink({ doi, href, fallback = '-' }) {
  if (!doi || !href) {
    return fallback
  }

  return (
    <Link
      href={href}
      className="block w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 hover:after:w-full after:bg-[currentColor]"
    >
      {doi}
    </Link>
  )
}
